import { Injectable, inject } from '@angular/core';
import { map, Observable, of } from 'rxjs';
import { AuthorizationRepository } from '../../../infrastructure/repositories/authorization.repository';
import { InvestigationAuthorizationState } from '../../states/investigation-authorization.state';
import { InvestigationPermission } from 'src/app/auth/domain/enums/investigation-permission.enum';
import { InvestigationRole } from 'src/app/auth/domain/enums/investigation-role.enum';

/**
 * Use case for getting investigation permissions.
 * Fetches user's investigation role and permissions from the API, caches them in state,
 * and returns the permissions array.
 * Implements deduplication to prevent multiple simultaneous API calls.
 */
@Injectable({ providedIn: 'root' })
export class GetInvestigationPermissionsUseCase {
  private readonly repository = inject(AuthorizationRepository);
  private readonly state = inject(InvestigationAuthorizationState);
  private readonly context = 'investigations';

  /**
   * Gets investigation permissions for the current user.
   * Saves role and permissions to state, but returns only permissions.
   * Uses cached state unless explicitly forced to refresh.
   *
   * @param forceRefresh - If true, forces a fresh API call even if data is already loaded
   * @returns Observable that emits the user's investigation permissions
   */
  public execute(forceRefresh = false): Observable<InvestigationPermission[]> {
    // Use cache unless explicitly forced to refresh
    if (!this.state.isLoaded() || forceRefresh) {
      return this.repository.getCurrentUserAuthorization(this.context).pipe(
        map(authData => {
          const role = authData.role as InvestigationRole;
          const permissions = authData.permissions as InvestigationPermission[];
          this.state.setAuthorization(role, permissions, authData.roleName, authData.dashboardViewTypeName);
          return permissions;
        })
      );
    }
    return of(this.state.permissions());
  }
}
